import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

export interface AutoExpandingTextareaProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Fired on Enter without Shift. Enables the submit-on-enter behavior. */
  onSubmit?: () => void;
  placeholder?: string;
  /** Rows shown when empty. */
  minRows?: number;
  /** Growth ceiling; the field scrolls past this. */
  maxRows?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  name?: string;
  ariaLabel?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  sx?: SxProps<Theme>;
  testId?: string;
}

const LINE_HEIGHT_PX = 22;

/**
 * AutoExpandingTextarea - Borderless textarea that grows with its content.
 *
 * Atomic Level: Molecule
 *
 * Height is recomputed from `scrollHeight` on every value change and clamped
 * between `minRows` and `maxRows`. Enter submits and Shift+Enter inserts a
 * newline when `onSubmit` is provided, matching chat-style inputs.
 *
 * @example
 * ```tsx
 * <AutoExpandingTextarea
 *   value={query}
 *   onValueChange={setQuery}
 *   onSubmit={runSearch}
 *   placeholder="Try: 3 bed condo under 900k in Toronto"
 * />
 * ```
 */
export const AutoExpandingTextarea = forwardRef<HTMLTextAreaElement, AutoExpandingTextareaProps>(
  (
    {
      value,
      onValueChange,
      onSubmit,
      placeholder,
      minRows = 1,
      maxRows = 6,
      disabled = false,
      autoFocus = false,
      name,
      ariaLabel,
      onFocus,
      onBlur,
      onKeyDown,
      sx,
      testId,
    },
    ref
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    // Measure against a collapsed height so the field can shrink as well as grow.
    useLayoutEffect(() => {
      const node = innerRef.current;
      if (!node) {
        return;
      }
      node.style.height = 'auto';
      const maxHeight = maxRows * LINE_HEIGHT_PX;
      const nextHeight = Math.min(node.scrollHeight, maxHeight);
      node.style.height = `${nextHeight}px`;
      node.style.overflowY = node.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [value, maxRows]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented || !onSubmit) {
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        onSubmit();
      }
    }, [onKeyDown, onSubmit]);

    return (
      <Box
        component="textarea"
        ref={innerRef}
        value={value}
        name={name}
        rows={minRows}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        data-testid={testId}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onValueChange(event.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        sx={{
          width: '100%',
          resize: 'none',
          border: 'none !important',
          outline: 'none',
          padding: 0,
          margin: 0,
          backgroundColor: 'transparent',
          color: '#1a1a1a',
          fontFamily: 'inherit',
          fontSize: '15px',
          lineHeight: `${LINE_HEIGHT_PX}px`,
          '&::placeholder': { color: '#909090' },
          '&:disabled': { color: '#909090', cursor: 'not-allowed' },
          ...sx,
        }}
      />
    );
  }
);

AutoExpandingTextarea.displayName = 'AutoExpandingTextarea';

export default AutoExpandingTextarea;
